<script setup lang="js">
import axios from "axios";
import { computed, defineProps, getCurrentInstance, onMounted, ref, watch } from "vue";
import { Formatter } from "@/utilities/Formatter";

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  tone: {
    type: String,
    required: false,
    default: "info"
  },
  method: {
    type: String,
    required: false,
    default: null
  },
  start: {
    type: String,
    required: false,
    default: null
  },
  end: {
    type: String,
    required: false,
    default: null
  },
  resultData: {
    type: Object,
    required: false,
    default: null
  },
  loading: {
    type: Boolean,
    required: false,
    default: false
  },
  error: {
    type: Boolean,
    required: false,
    default: false
  },
  eyebrow: {
    type: String,
    required: false,
    default: ""
  },
  leftMetaLabel: {
    type: String,
    required: false,
    default: ""
  },
  leftMetaValue: {
    type: [String, Number],
    required: false,
    default: null
  }
});

const localResult = ref(null);
const localLoading = ref(false);
const localError = ref(false);
const { proxy } = getCurrentInstance();
const t = (key) => proxy.$filters.localize(key);

const isExternal = computed(() => props.resultData !== null || props.method === null);
const result = computed(() => (isExternal.value ? props.resultData : localResult.value));
const loading = computed(() => (isExternal.value ? props.loading : localLoading.value));
const hasError = computed(() => (isExternal.value ? props.error : localError.value));
const toneClass = computed(() => `orders-status--${props.tone}`);
const eyebrowDisplay = computed(() => props.eyebrow || t("Dashboard.ordersStatus.eyebrow"));
const leftMetaLabelDisplay = computed(() => props.leftMetaLabel || t("Dashboard.ordersStatus.retailCount"));
const leftMetaDisplay = computed(() =>
  props.leftMetaValue !== null && props.leftMetaValue !== undefined
    ? props.leftMetaValue
    : result.value?.retailCount ?? 0
);

const metrics = computed(() => {
  if (!result.value) {
    return [];
  }

  return [
    { label: t("Dashboard.metrics.turnover"), value: Formatter.toCurrency(result.value.turnoverTotal) },
    { label: t("Dashboard.metrics.profit"), value: Formatter.toCurrency(result.value.profitTotal) },
    { label: t("Dashboard.metrics.purchase"), value: Formatter.toCurrency(result.value.purchaseAmount) },
    { label: t("Dashboard.metrics.averageCheck"), value: Formatter.toCurrency(result.value.averageCheck) }
  ];
});

const load = async () => {
  if (isExternal.value || !props.method) {
    return;
  }

  try {
    localResult.value = null;
    localError.value = false;
    localLoading.value = true;
    const response = await axios.get(
      `${window.location.origin}/ui/v3/dashboard/${props.method}?start=${props.start}&end=${props.end ?? ""}`
    );
    localResult.value = response.data;
  } catch {
    localError.value = true;
  } finally {
    localLoading.value = false;
  }
};

onMounted(load);
watch(() => props.start, load);
watch(() => props.end, load);
</script>

<template>
  <article class="orders-status" :class="toneClass">
    <template v-if="result">
      <div class="orders-status__header">
        <div>
          <div class="orders-status__eyebrow">{{ eyebrowDisplay }}</div>
          <h3 class="orders-status__title">{{ title }}</h3>
        </div>
        <div class="orders-status__count">{{ result.count }}</div>
      </div>

      <div class="orders-status__subline">
        <span>{{ leftMetaLabelDisplay }}: {{ leftMetaDisplay }}</span>
        <span>{{ $filters.localize("Dashboard.metrics.marginPercent") }}: {{ Number(result.marginPercent ?? 0).toFixed(2) }}%</span>
      </div>

      <div class="orders-status__metrics">
        <div v-for="metric in metrics" :key="metric.label" class="orders-status__metric">
          <div class="orders-status__metric-label">{{ metric.label }}</div>
          <div class="orders-status__metric-value">{{ metric.value }}</div>
        </div>
      </div>
    </template>

    <template v-else-if="hasError">
      <div class="dashboard-state dashboard-state--error">
        {{ $filters.localize("Dashboard.states.loadError") }}
      </div>
    </template>

    <template v-else>
      <div class="dashboard-skeleton dashboard-skeleton--card">
        <div class="dashboard-skeleton__line dashboard-skeleton__line--title"></div>
        <div class="dashboard-skeleton__line dashboard-skeleton__line--meta"></div>
        <div class="dashboard-skeleton__grid">
          <div v-for="index in 4" :key="index" class="dashboard-skeleton__tile"></div>
        </div>
      </div>
    </template>

    <div v-if="loading && result" class="dashboard-loader-overlay">
      <div class="dashboard-loader"></div>
    </div>
  </article>
</template>

<style scoped>
.orders-status {
  position: relative;
  min-height: 300px;
  padding: 1.25rem;
  border-radius: 24px;
  border: 1px solid rgba(23, 50, 77, 0.08);
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 40px rgba(23, 50, 77, 0.08);
  overflow: hidden;
}

.orders-status--info {
  background: #eef7ff;
}

.orders-status--success {
  background: #edf9f2;
}

.orders-status--danger {
  background: #fff1f1;
}

.orders-status--tailoring {
  background: #eefbf5;
}

.orders-status__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.orders-status__eyebrow {
  color: #6f8194;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.35rem;
  font-weight: 700;
}

.orders-status__title {
  margin: 0;
  color: #17324d;
  font-size: 1.3rem;
}

.orders-status__count {
  min-width: 3rem;
  height: 3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  color: #17324d;
  font-size: 1.1rem;
  font-weight: 700;
}

.orders-status__subline {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.9rem;
  color: #6f8194;
  font-weight: 600;
}

.orders-status__metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 1rem;
}

.orders-status__metric {
  padding: 0.85rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
}

.orders-status__metric-label {
  margin-bottom: 0.35rem;
  color: #6f8194;
  font-size: 0.82rem;
}

.orders-status__metric-value {
  color: #17324d;
  font-weight: 700;
  line-height: 1.25;
}

.dashboard-state {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  border-radius: 22px;
  text-align: center;
  font-weight: 600;
}

.dashboard-state--error {
  border: 1px dashed rgba(138, 91, 91, 0.24);
  background: rgba(255, 246, 246, 0.92);
  color: #8a5b5b;
}

.dashboard-skeleton--card {
  min-height: 240px;
}

.dashboard-skeleton__line,
.dashboard-skeleton__tile {
  background: linear-gradient(90deg, #edf4fb 0%, #f8fbfe 50%, #edf4fb 100%);
  background-size: 200% 100%;
  animation: dashboard-shimmer 1.5s infinite linear;
  border: 1px solid rgba(23, 50, 77, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.dashboard-skeleton__line {
  height: 14px;
  border-radius: 999px;
}

.dashboard-skeleton__line--title {
  width: 56%;
  height: 24px;
}

.dashboard-skeleton__line--meta {
  width: 40%;
  margin-top: 0.85rem;
}

.dashboard-skeleton__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 1rem;
}

.dashboard-skeleton__tile {
  min-height: 72px;
  border-radius: 18px;
}

.dashboard-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(2px);
}

.dashboard-loader {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 3px solid rgba(23, 50, 77, 0.12);
  border-top-color: #17324d;
  animation: dashboard-spin 0.8s linear infinite;
}

@keyframes dashboard-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@keyframes dashboard-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767px) {
  .orders-status__metrics,
  .dashboard-skeleton__grid {
    grid-template-columns: 1fr;
  }
}
</style>
